// ============================================================
// PERRY — QTE ENGINE (pure logic, no React, no Firebase)
// Seeded attack rolling, angle rolling, timing grades, chain math.
// The director drives it; the stage performs it; the overlay shows
// almost none of it. None of the three re-implements the others.
//
// THE OVERHAUL, 2026-07-26 (caP'n): there is no prompt glyph and
// no ring. A strike is a THING THE FOE DOES, from an ANGLE, and
// the answer is that angle on the guard rose — S D F high, J K L
// low. Everything in this file exists to make that fair.
//
// DETERMINISM LAW: mulberry32, never Math.random(). The run's
// seed is fixed at Solrend start, so a fight is reproducible and
// testable inside one run — and different every run, because the
// seed moves. That is what makes it a battle and not a script.
// ============================================================

import { QTE, GUARD, ATTACKS, RULES, APPROACH } from "./perryData";

// ── mulberry32 — COPY INTO EVERY IE 3D GAME VERBATIM ─────────
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);

// ── Weighted roll out of a moveset ───────────────────────────
// `aggro` is how many Anima the foe has lost. A wounded thing
// lies MORE and winds up FASTER — the same escalation curve on
// both axes, so the fight gets meaner exactly as you get closer.
export function rollAttack(r, moveset, lastId = null, aggro = 0, repeats = 0) {
  const table = [];
  for (const [id, w0] of Object.entries(moveset || {})) {
    if (!ATTACKS[id]) continue;
    let w = w0;
    // wounded → likes lying, likes the big one
    if (id === "feint") w *= Math.pow(1 / RULES.AGGRO_LIE, aggro);
    if (id === "calamity") w *= 1 + aggro * 0.35;
    // never three of the same thing in a row — the one kindness,
    // inherited from the old key-pool rule
    if (id === lastId && repeats >= 1) w *= 0.25;
    if (w > 0) table.push([id, w]);
  }
  if (!table.length) return "swipe";
  const total = table.reduce((a, [, w]) => a + w, 0);
  let roll = r() * total;
  for (const [id, w] of table) {
    roll -= w;
    if (roll <= 0) return id;
  }
  return table[table.length - 1][0];
}

// ── Angle roll — never the same angle twice running ──────────
export function rollAngle(r, attackId, lastAngle = null) {
  const atk = ATTACKS[attackId] || ATTACKS.swipe;
  const pool = atk.angles.filter((a) => a !== lastAngle);
  const use = pool.length ? pool : atk.angles;
  return use[Math.floor(r() * use.length)];
}

// The mirror of an angle — a Slash's second beat comes back the
// other way, which is why it reads as one motion instead of two.
// Relaid 2026-07-27 with the grid. The mirror is now literally
// "the other hand, same finger" — F↔J, D↔K, S↔L — which is a far
// better Slash than the old one: the second beat lands on the
// matching finger of your other hand, so one motion, two paws.
const MIRROR = { F: "J", J: "F", D: "K", K: "D", S: "L", L: "S" };
export const mirrorAngle = (a) => MIRROR[a] || a;

// ── THE CHORD ROLL — added 2026-07-28 (R-25, finally) ────────
// One key from EACH HAND. That is the whole rule, and it is the
// rule only because the Grid Relay made columns mean hands — on
// the old rose a chord was two fingers of one hand and there was
// no shippable version of it.
//
// The two halves are drawn from the attack's own `angles` (left
// column) and `angles2` (right column), so a chording foe can be
// as narrow or as wide as its design wants. Never the same pair
// twice running, for the same reason no angle repeats: a pattern
// the player can predict is a pattern the player stops reading.
export function rollChord(r, attackId, lastPair = null) {
  const atk = ATTACKS[attackId] || ATTACKS.cleft;
  const L = (atk.angles || GUARD.LEFT).filter((k) => GUARD.LEFT.includes(k));
  const R2 = (atk.angles2 || GUARD.RIGHT).filter((k) => GUARD.RIGHT.includes(k));
  const left = L.length ? L : GUARD.LEFT;
  const right = R2.length ? R2 : GUARD.RIGHT;
  for (let guard = 0; guard < 12; guard++) {
    const a = left[Math.floor(r() * left.length)];
    const b = right[Math.floor(r() * right.length)];
    if (!lastPair || a !== lastPair[0] || b !== lastPair[1]) return [a, b];
  }
  return [left[0], right[0]];
}

// Is this key one half of the live chord, and which half?
export const chordHalf = (st, k) =>
  (st?.chord ? (k === st.chord[0] ? 0 : k === st.chord[1] ? 1 : -1) : -1);

// ── Wind-up scaling ──────────────────────────────────────────
// tempo (the foe's natural speed) × aggro tightening × any Impera
// slow, floored so nothing ever becomes unreadable.
export function windScale(tempo = 1, aggro = 0, slowed = false) {
  let s = tempo * Math.pow(1 - RULES.AGGRO_TIGHTEN, aggro);
  s = Math.max(RULES.AGGRO_FLOOR, s);
  if (slowed) s *= 1.35;                // Still the Flame
  return s;
}

// ── STRIKE FACTORY ───────────────────────────────────────────
// A strike is everything the stage needs to PERFORM it and
// everything the grader needs to JUDGE it, in one object.
//
//   spawnAt   the foe starts winding up (the arc appears)
//   travelAt  (hurl only) the moment the orb leaves its hand
//   impactAt  the moment it arrives — the beat you answer
//   commitAt  after this, a wrong angle is a real MISS
export function makeStrike(attackId, angle, now, opts = {}) {
  const atk = ATTACKS[attackId] || ATTACKS.swipe;
  const { scale = 1, cal = null, echo = false } = opts;
  const wind = atk.wind * scale;
  const travel = (atk.travel || 0) * scale;
  const impactAt = now + wind + travel;
  const span = Math.max(1, wind + travel);
  const st = {
    id: atk.id,
    atk,
    angle,                                 // a lie still has an angle it lies FROM
    feint: !!atk.feint,
    arc: atk.arc,
    tell: atk.tell,
    scale,
    cal,                                   // { idx, total } inside a Calamity
    echo,                                  // the second beat of a Slash
    body: atk.body || "swing",             // what the FOE does (Exception Law)
    projectile: atk.projectile || null,    // what the STAGE draws
    spawnAt: now,
    travelAt: travel ? now + wind : null,
    impactAt,
    commitAt: impactAt - QTE.COMMIT_MS * scale,
    answered: false,
    // ── THE CHORD ── two angles, one per hand, both required.
    // `angle` stays the LEFT half so that every single consumer
    // already written against `strike.angle` — the arc, the lane,
    // the foe's twist, the sparks, the coal — keeps working with
    // no edit at all, and the right half is purely additive.
    chord: opts.chord || null,
    downAt: opts.chord ? [0, 0] : null,
    // ── THE HOLD ── ms of arc to RIDE after meeting the beat.
    // Zero means "this is a beat, meet it and let go", which is
    // every strike written before today.
    holdMs: 0,
    holding: null,
  };
  if (atk.hold) st.holdMs = atk.hold * scale;
  if (opts.chord) { st.angle = opts.chord[0]; st.angle2 = opts.chord[1]; }

  // ── THE WRAP's four moments ──
  // Absolute stamps for the stage (which owns a clock) AND the
  // same moments normalized into `prog` for the body (which only
  // ever sees how far through the strike it is). Deriving both
  // from one place is why the blade can never leave the hand on a
  // different frame than the hand opens.
  if ((atk.path || "step") === "wrap") {
    const castAt = now + wind * (atk.cast ?? 0.24);
    const behindAt = now + wind * (atk.behind ?? 0.62);
    st.castAt = castAt;
    st.behindAt = behindAt;
    st.yankAt = now + wind;                // the chain goes tight
    st.fr = {
      cast: (castAt - now) / span,
      behind: (behindAt - now) / span,
      yank: wind / span,
    };
  }
  return st;
}

// ══════════════════════════════════════════════════════════════
//  MASTERY — "I HAVE LEARNED TO SWING THIS THING" (caP'n, 2026-07-30)
// ══════════════════════════════════════════════════════════════
// A heavy Treasure does NOT slow your swing. It narrows the moment
// your answer counts — that is what `windowMod` has always been,
// and caP'n confirmed it reads exactly right: you pay a price for
// carrying something enormous.
//
// THE BUG THIS FIXES. Windows resolve as `(1 + grace) * widen`,
// multiplicatively. Every high-Lyt Treasure in the game carries a
// NEGATIVE windowMod, so the two terms fought each other:
//
//   Fire-Hardened Reed  + Read the Flame 3  →  +24.0%
//   Ash Cleaver         + Read the Flame 3  →  +11.6%
//   Sunken Bell's Clapper + Read the Flame 3 →  +6.6%
//
// An 8 ◆ endgame weapon left a maxed constellation with a THIRD
// of the timing the free starter branch gave it. A player could
// buy an upgrade that silently refunded two levels of Grace, and
// nothing on any screen said so. That is why 600 ⋔ of shopping
// felt like nothing: most of it was cancelling the rest.
//
// THE RULE. Read the Flame doesn't just widen windows — it teaches
// you the weapon. Each LEVEL forgives a third of your Treasure's
// weight penalty. Level 3 forgives all of it.
//
//   L0  you fight the thing as much as the foe
//   L1  ⅓ forgiven
//   L2  ⅔ forgiven
//   L3  gone — the Clapper becomes +24% windows AND its Lyt
//
// ONLY THE PENALTY IS FORGIVEN. A positive windowMod (the Censer's
// +18%, the Lantern Hook's +12%) is a gift the weapon gives and is
// never touched. And mastery is specifically about WEIGHT, so it
// does not touch `grip` — nobody ever learns to hold a bell still,
// and the Clapper says so on the label. That asymmetry is the
// whole character of the top shelf.
//
// This is what makes Cursed Weapons possible (R-51): a Treasure
// may now ship with a genuinely punishing windowMod, because there
// is a road to mastering it instead of a wall. (Dial: THIRDS.)
const MASTERY_STEPS = 3;   // levels of Read the Flame to full forgiveness

export function masteredWindowMod(windowMod = 0, graceLv = 0) {
  if (!windowMod || windowMod >= 0) return windowMod || 0;   // gifts are never taxed
  const lv = Math.max(0, Math.min(MASTERY_STEPS, graceLv || 0));
  return windowMod * (1 - lv / MASTERY_STEPS);
}

// ── The window bands for THIS strike, with everything applied ──
// mods: { grace, graceLv, goodWide, perfectTight, windowMod, bias{}, hurlMod }
export function bandsFor(strike, mods = {}) {
  const {
    grace = 0, graceLv = 0, goodWide = 0, perfectTight = 0,
    windowMod = 0, bias = {}, hurlMod = 0,
  } = mods;
  const atk = strike.atk || ATTACKS.swipe;
  const s = strike.scale || 1;
  let widen = 1 + masteredWindowMod(windowMod, graceLv) + (bias[strike.angle] || 0);
  if (atk.id === "hurl") widen += hurlMod;
  widen = Math.max(0.4, widen);           // no Treasure may make it impossible
  return {
    perfect: QTE.PERFECT_MS * s * (atk.perfect || 1) * (1 + grace) * widen * (1 - perfectTight),
    early: QTE.GOOD_EARLY_MS * s * (atk.good || 1) * (1 + grace + goodWide) * widen,
    late: QTE.GOOD_LATE_MS * s * (atk.good || 1) * (1 + grace + goodWide) * widen,
  };
}

// ── HOW LONG YOU ACTUALLY HAVE TO STAND THERE ────────────────
// The attack states the arc; the Treasure and the constellation
// state how good you are at standing in one. Positive grip SHORTENS
// the ride (the Mire Fork was built for this); negative grip
// LENGTHENS it (the Sunken Bell's Clapper very much was not, and
// says so on the label). Floored, so nothing can make a hold
// vanish and nothing can make it unsurvivable.
export function holdWindow(strike, mods = {}) {
  if (!strike?.holdMs) return 0;
  const grip = mods.grip || 0;
  return Math.max(180, Math.min(2200, strike.holdMs * (1 - grip)));
}

// ── GRADING — the one place a parry is judged ────────────────
// Returns:
//   "PERFECT" | "GOOD" | "MISS"
//   "EARLY"   — pressed before the window opened. Ignored, never punished.
//   "PROBE"   — wrong angle, but early enough to still fix it. Free.
//
// THE COMMIT RULE is what makes six angles humane: guessing wrong
// while the arc is still travelling costs you nothing at all.
// Guessing wrong in the last breath costs you everything.
export function gradePress(strike, pressedKey, now, mods = {}) {
  if (!strike || strike.answered) return "EARLY";
  if (!GUARD.KEYS.includes(pressedKey)) return "EARLY";
  if (now - strike.spawnAt < QTE.INPUT_OPEN_MS) return "EARLY";

  // A lie: any answer at all is wrong. That is the entire point.
  if (strike.feint) return "MISS";

  // ── ALREADY RIDING ──
  // Re-pressing the key you are holding is not a new answer, it is
  // a key bouncing. Without this guard a release-and-recatch inside
  // HOLD_SLACK_MS would re-grade against an impact that is already
  // several hundred milliseconds behind and hand back a MISS for
  // doing exactly the right thing.
  if (strike.holding) {
    if (pressedKey === strike.holding.key) return "RIDING";
    return now < strike.commitAt ? "PROBE" : "MISS";
  }

  // ══════════════════════════════════════════════════════════
  //  THE CHORD — two angles, one instant, one hand each
  // ══════════════════════════════════════════════════════════
  // Returns "PARTIAL" for the first half, which the director
  // treats as "nothing has happened yet" — no grade, no bite, no
  // chain break, the strike stays live. The SECOND half is what
  // gets graded, so a pair that lands together grades on its
  // slower hand and a pair that does not land together does not
  // land at all.
  if (strike.chord) {
    const half = strike.chord[0] === pressedKey ? 0 : strike.chord[1] === pressedKey ? 1 : -1;
    if (half < 0) return now < strike.commitAt ? "PROBE" : "MISS";
    if (!strike.downAt[half]) strike.downAt[half] = now;
    const other = strike.downAt[1 - half];
    if (!other) return "PARTIAL";                       // one paw down, waiting
    if (Math.abs(now - other) > QTE.CHORD_MS) return "MISS";  // not together
    return gradeAt(strike, Math.max(now, other), mods);
  }

  if (pressedKey !== strike.angle) {
    return now < strike.commitAt ? "PROBE" : "MISS";
  }

  const g = gradeAt(strike, now, mods);
  // ══════════════════════════════════════════════════════════
  //  THE HOLD — meeting it is only half of answering it
  // ══════════════════════════════════════════════════════════
  // A graded press against a holding strike does not resolve. It
  // LATCHES: the grade is banked, the strike stays live, and the
  // director watches the key. Let go before the arc ends and the
  // banked grade is thrown away and the rest of it lands.
  if (strike.holdMs && g !== "MISS") {
    strike.holding = { key: pressedKey, from: now, grade: g, until: strike.impactAt + holdWindow(strike, mods) };
    return "RIDING";
  }
  return g;
}

// The timing half of a grade, split out so the chord can ask for a
// verdict at a moment that is not "now" — the later of two paws.
function gradeAt(strike, at, mods = {}) {
  const b = bandsFor(strike, mods);
  const delta = at - strike.impactAt;     // negative = early
  if (Math.abs(delta) <= b.perfect) return "PERFECT";
  if (delta >= -b.early && delta <= b.late) return "GOOD";
  return "MISS";
}

// Has the strike arrived and gone with no answer?
export function isTimedOut(strike, now) {
  if (!strike) return false;
  const s = strike.scale || 1;
  if (strike.feint) return now >= strike.spawnAt + QTE.FEINT_HOLD_MS * s;
  // A strike being RIDDEN is not over when it arrives — it is over
  // when it stops. The director resolves the ride itself; this only
  // has to stop calling it a miss in the meantime.
  if (strike.holding) return now >= strike.holding.until;
  return now >= strike.impactAt + QTE.MISS_TIMEOUT_MS * s;
}

// ── SHIFTING A STRIKE THROUGH A PAUSE ────────────────────────
// Added 2026-07-29 with the Tutorial's report card. A strike is a
// bag of absolute performance.now() deadlines, and the wall clock
// does not stop when the game does — so every stamp on it moves
// forward by however long we stood still.
//
// EVERY TIME FIELD IS LISTED HERE, AND THAT IS THE POINT. If a
// future attack grows a fifth moment (the wrap already has four),
// it belongs on this list the same day it is written, or the
// pause will silently un-time that one thing and nothing else.
// `fr` is deliberately absent: those are FRACTIONS of the span,
// not moments, and a fraction does not move.
export function shiftStrike(strike, d) {
  if (!strike || !d) return strike;
  const s = strike;
  s.spawnAt += d;
  s.impactAt += d;
  s.commitAt += d;
  if (s.travelAt) s.travelAt += d;
  if (s.castAt) s.castAt += d;
  if (s.behindAt) s.behindAt += d;
  if (s.yankAt) s.yankAt += d;
  if (s.holding) {
    s.holding.from += d;
    s.holding.until += d;
  }
  if (s.downAt) {
    if (s.downAt[0]) s.downAt[0] += d;
    if (s.downAt[1]) s.downAt[1] += d;
  }
  return s;
}

// ── LETTING GO OF A HOLD ─────────────────────────────────────
// The other half of the ride, and the only place a release is
// judged. You met the arc and latched; this decides whether you
// STAYED in it.
//
//   too early  → the banked grade is thrown away and the rest of
//                the jet goes through the gap you opened. MISS.
//   inside the slack → a keyboard bounce is not a decision. The
//                ride stands, and the strike is still live.
//   past the arc → you rode the whole thing. The banked grade is
//                what you get, and it is what you earned when you
//                met the beat — riding well does not upgrade a
//                GOOD into a PERFECT, it only keeps it.
//
// Returns "MISS" | "RIDDEN" | null (null = nothing happened yet).
export function gradeRelease(strike, releasedKey, now) {
  if (!strike || !strike.holding) return null;
  if (releasedKey !== strike.holding.key) return null;
  const left = strike.holding.until - now;
  if (left <= QTE.HOLD_SLACK_MS) return "RIDDEN";
  return "MISS";
}

// How far through its arc a strike is: 0 at wind-up, 1 at impact.
// The stage paints the telegraph with this, and it is the ONLY
// timing information the player gets. It lives in the world.
export function strikeProgress(strike, now) {
  if (!strike) return 0;
  const span = strike.impactAt - strike.spawnAt;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1.3, (now - strike.spawnAt) / span));
}

// ── Treasure → grading mods ──────────────────────────────────
export function treasureMods(tr) {
  if (!tr) return { windowMod: 0, bias: {}, hurlMod: 0, grip: 0 };
  return {
    windowMod: tr.windowMod || 0,
    bias: tr.bias || {},
    hurlMod: tr.hurlMod || 0,
    grip: tr.grip || 0,        // hold tolerance only — added with the ride
  };
}

// ── Species timing mods (temporal % — allowed by the Small
//    Number Law's boundary table) ─────────────────────────────
export function speciesMods(speciesId) {
  if (speciesId === "shadow_fox") return { goodWide: 0.10 };
  if (speciesId === "chaos_dragon") return { perfectTight: 0.08 };
  return {};
}

// ── Chain milestones — the Threadmark numbers live here ──────
export const CHAIN_MILESTONES = [10, 30, 50];


// ════════════════════════════════════════════════════════════
// THE SCREEN SOLVER — added 2026-07-27
// ════════════════════════════════════════════════════════════
// Everything about a strike's geometry resolves through these two
// functions and NOTHING else. That is the point: the guard, the
// telegraph, the sparks, the foe's own feet and the training rose
// all ask the same question of the same code, so they cannot
// disagree about where "left" is. The bug this replaces was
// exactly that disagreement — the guard used world X, the foe
// used its own local X (which a 90° yaw had turned into world Z),
// and the arc used the line between them. Three answers, one
// question, none of them on screen where the key said.
//
// NO THREE IMPORT. These read the camera's world matrix directly
// as numbers, so this file stays pure logic and can be imported
// by anything without dragging the renderer along.
//
//   matrixWorld.elements is column-major:
//     [0][1][2]  → the camera's RIGHT vector (unit)
//     [4][5][6]  → the camera's UP vector (unit)
// ════════════════════════════════════════════════════════════

// Where the angle `k` lives, in world space, for the CURRENT
// camera. `base` is Perry (any {x,y,z}). `out` is written in
// place and returned — call it every frame, allocate nothing.
export function roseWorld(camera, base, k, out) {
  const d = GUARD.DIRS[k] || GUARD.DIRS.D;
  const m = camera.matrixWorld.elements;
  const sx = d.ax * GUARD.SPREAD_X;
  const sy = d.ay * GUARD.SPREAD_Y;
  out.x = base.x + m[0] * sx + m[4] * sy;
  out.y = (base.y || 0) + GUARD.CENTER_Y + m[1] * sx + m[5] * sy;
  out.z = base.z + m[2] * sx + m[6] * sy;
  return out;
}

// Where a foe must STAND to deliver angle `k` honestly — the
// lane. Same camera basis, so "the enemy on your left" and "the
// guard on your left" are the same left, always. Centre angles
// (D / K) put it square in front, because they arrive on the
// vertical and don't own a side.
export function laneWorld(camera, base, k, out) {
  const d = GUARD.DIRS[k] || GUARD.DIRS.D;
  const m = camera.matrixWorld.elements;
  // push AWAY from the lens as well as sideways, so a foe in a
  // lane never parks itself between the camera and Perry.
  // A centre attacker (D / K) still stands off to one side. Dead
  // centre would park it directly behind Perry from the lens and
  // hide the only thing the player is trying to read.
  // Every angle owns a side now — there is no centre to special-
  // case, and no foe can end up parked between the lens and Perry.
  const side = d.lane * APPROACH.LANE_X;
  const up = APPROACH.LANE_Z;
  // (m8,m9,m10) is the camera's BACKWARD vector — it points at the
  // viewer. Upstage is therefore MINUS it.
  out.x = base.x + m[0] * side - m[8] * up;
  out.y = 0;
  out.z = base.z + m[2] * side - m[10] * up;
  return out;
}

// The bulge point of a HOOK — how caP'n's boomerang gets from a
// foe standing on your right to a blow arriving on your left.
// The control point is thrown far outside the frame on the
// OPPOSITE side of the target, so the curve visibly leaves, goes
// around, and comes back in pointing the right way.
export function hookControl(camera, base, k, out) {
  const d = GUARD.DIRS[k] || GUARD.DIRS.D;
  const m = camera.matrixWorld.elements;
  const side = d.lane * APPROACH.HOOK_OUT;
  const lift = d.tier === "top" ? 1.5 : d.tier === "mid" ? 0.85 : 0.28;
  out.x = base.x + m[0] * side;
  out.y = (base.y || 0) + lift;
  out.z = base.z + m[2] * side;
  return out;
}

// ── THE WRAP — added 2026-07-27 with the Chain Kunai ─────────
// Where a thrown blade goes when it is thrown PAST you. It is the
// only place in the fight geometry that resolves BEHIND the guard
// rose, and it is deliberately camera-relative like everything
// else: "past your ear" has to mean past your ear on the glass,
// or the whole trick reads as the blade simply leaving.
//
// It flies away from the foe (so it visibly passes Perry rather
// than orbiting him), further out than any lane, and lifted — a
// thing hanging in the dark on a slack chain, not a thing lying
// in the road.
export function wrapBehind(camera, base, foe, out) {
  const m = camera.matrixWorld.elements;
  // the screen-lateral component of (Perry − foe): which way is
  // "away from the thing that threw it", side-to-side, on screen
  const dx = base.x - (foe?.x ?? base.x);
  const dz = base.z - (foe?.z ?? base.z);
  let lat = m[0] * dx + m[2] * dz;
  if (Math.abs(lat) < 0.001) lat = 1;
  const s = (lat > 0 ? 1 : -1) * APPROACH.WRAP_BEHIND;
  // and downstage, toward the lens, so it clears his shoulder
  const down = APPROACH.WRAP_BEHIND * 0.5;   // ...and toward the lens
  out.x = base.x + m[0] * s + m[8] * down;
  out.y = (base.y || 0) + GUARD.CENTER_Y + APPROACH.WRAP_LIFT;
  out.z = base.z + m[2] * s + m[10] * down;
  return out;
}

// The bulge of the RETURN. Thrown wide outside the frame on the
// side the blade is coming back from, so the haul reads as one
// long whip around Perry rather than a straight line home. The
// bezier's tangent at the end IS the arrival angle, same law the
// hook is built on.
export function wrapControl(camera, base, k, out) {
  const d = GUARD.DIRS[k] || GUARD.DIRS.D;
  const m = camera.matrixWorld.elements;
  const side = d.lane * APPROACH.WRAP_OUT;
  const lift = d.tier === "top" ? 1.75 : d.tier === "mid" ? 0.95 : 0.22;
  out.x = base.x + m[0] * side;
  out.y = (base.y || 0) + lift;
  out.z = base.z + m[2] * side;
  return out;
}

// Quadratic bezier, written in place. The one curve the whole
// fight is drawn with — hooks, orbs and overhead falls are all
// this with a different control point.
export function bez(a, c, b, t, out) {
  const u = 1 - t;
  const w0 = u * u, w1 = 2 * u * t, w2 = t * t;
  out.x = a.x * w0 + c.x * w1 + b.x * w2;
  out.y = a.y * w0 + c.y * w1 + b.y * w2;
  out.z = a.z * w0 + c.z * w1 + b.z * w2;
  return out;
}

// The path a given strike travels on. Reads the attack's own
// declaration; the echo beat of a Slash gets its own.
export function pathOf(strike) {
  if (!strike) return "step";
  const A = ATTACKS[strike.id];
  if (!A) return "step";
  if (strike.echo && A.echoPath) return A.echoPath;
  return A.path || "step";
}

// ════════════════════════════════════════════════════════════
// THE SWING — added 2026-07-27
// ════════════════════════════════════════════════════════════
// The one timeline a parry is performed on. It lives here, with
// the other solvers, for exactly the reason `roseWorld` does: the
// ARM and the TREASURE were each running their own private curve
// (a `dt*24` lerp and a `sin(life)`), which is why an answer read
// as two unrelated things twitching rather than one motion. Now
// the limb, the blade, the trail, the torso and the tail all ask
// this function the same question and get the same number back,
// so they cannot fall out of phase.
//
// PURE. No THREE, no React, no allocation — `out` is written in
// place and returned, so a fight costs zero garbage.
//
// The shape, in order, and why each piece is there:
//
//   WIND    anticipation. The paw pulls AWAY from the angle first.
//           Nothing in animation sells force like moving backward
//           before you move forward. ~54 ms — under the threshold
//           where it would feel like input lag.
//   SWING   the whip. easeInOutCubic, so peak speed lands at the
//           MIDDLE of the travel (that's where the trail ribbon
//           belongs) and it DECELERATES into contact — which is
//           what "meeting" a blow looks like, as opposed to
//           "arriving at a coordinate."
//   STOP    hitstop. The whole rig freezes on the contact frame
//           while the sparks keep flying. Six lines of code and it
//           is the single largest felt-weight win in the file.
//           A MISS gets none: nothing stopped, so nothing stops.
//   SETTLE  follow-through. A damped spring that OVERSHOOTS the
//           angle and comes back, instead of parking on it.
//
// `reach` is deliberately allowed outside 0..1 — negative through
// the wind, past 1 through the overshoot — because the consumer
// feeds it to a bezier that extrapolates. The cock and the
// follow-through therefore come for free off the same curve.
export const SWING = {
  WIND_MS: 54,
  STRIKE_MS: 132,     // contact. The angle is MET at this age.
  SETTLE_MS: 330,
  TAIL_MS: 560,       // the performance is over; hold the pose
  COCK: 0.20,         // how far back the anticipation pulls
  OVER: 0.19,         // how far past the angle the follow-through goes
  BOW: 0.46,          // how fat the swing arc is (0 = a slide, which is what we had)
  // Per-grade hitstop, ms. A Calamity's Fracture gets the longest
  // freeze in the game because it is the biggest thing you can do.
  STOP: { PERFECT: 64, FRACTURE: 92, GOOD: 38, HELD: 24, MISS: 0, NONE: 0 },
  // Per-grade amplitude. NONE is a guard thrown at nothing — it
  // still swings, just without conviction.
  POWER: { PERFECT: 1.00, FRACTURE: 1.18, GOOD: 0.72, HELD: 0.50, MISS: 0.36, NONE: 0.46 },
  BREAK_MS: 260,      // how long a broken guard takes to recover
};

const easeInOutCubic = (x) =>
  x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

// age: ms since the press was latched. grade: null while unjudged.
// Writes and returns `out`:
//   reach    -0.20 → 1.19 → 1.00   position along the swing
//   bow       0 → 1 → 0            how far off the straight line
//   imp       1 → 0                the contact pulse
//   ring      decaying vibration   metal that just ate fire
//   broke     0 → 1 → 0            a MISS driving the guard through
//   hitstop   1 during the freeze
//   power     grade amplitude      · live: is anything happening
export function swingEnvelope(age, grade, out) {
  const o = out || {};
  const g = grade || "NONE";
  const power = SWING.POWER[g] ?? SWING.POWER.NONE;
  const stop = SWING.STOP[g] ?? 0;

  o.power = power;
  o.grade = grade || null;
  o.reach = 1; o.bow = 0; o.imp = 0; o.ring = 0;
  o.broke = 0; o.hitstop = 0; o.live = false; o.phase = "rest";

  if (age < 0 || age > SWING.TAIL_MS + stop) return o;
  o.live = true;

  // Fold the freeze out of the timeline. Everything downstream is
  // written against a clock that simply stopped for a moment.
  let a = age;
  if (a >= SWING.STRIKE_MS) {
    if (a < SWING.STRIKE_MS + stop) { o.hitstop = 1; a = SWING.STRIKE_MS; }
    else a -= stop;
  }

  if (a < SWING.WIND_MS) {
    const b = a / SWING.WIND_MS;
    o.reach = -SWING.COCK * power * (1 - (1 - b) * (1 - b));
    o.phase = "wind";
    return o;
  }

  if (a < SWING.STRIKE_MS) {
    const b = (a - SWING.WIND_MS) / (SWING.STRIKE_MS - SWING.WIND_MS);
    const c0 = -SWING.COCK * power;
    o.reach = c0 + (1 - c0) * easeInOutCubic(b);
    o.bow = SWING.BOW * power * Math.sin(b * Math.PI);
    o.phase = "swing";
    return o;
  }

  // ── past contact ──
  const s = (a - SWING.STRIKE_MS) / (SWING.SETTLE_MS - SWING.STRIKE_MS);
  const damp = Math.max(0, 1 - s);
  o.reach = 1 + SWING.OVER * power * Math.sin(s * Math.PI * 2.1) * damp * damp;
  o.phase = s < 1 ? "settle" : "hold";

  const ia = (a - SWING.STRIKE_MS) / 190;
  const fall = Math.max(0, 1 - ia);
  o.imp = fall * fall * power;
  o.ring = o.imp * Math.sin((a - SWING.STRIKE_MS) * 0.085);

  // A MISS is not a parry that graded badly — it is a guard that
  // got driven THROUGH. The blow keeps coming, the paw is knocked
  // back inside the angle, and the body eats it.
  if (g === "MISS") {
    o.broke = Math.sin(Math.min(1, (a - SWING.STRIKE_MS) / SWING.BREAK_MS) * Math.PI);
    o.reach = 1 - 0.58 * o.broke;
  }
  return o;
}

// Quadratic bezier on a single axis, extrapolating cleanly outside
// 0..1 — which is the whole trick. Feed it `reach` and the
// anticipation and the overshoot fall out of the same parabola the
// swing arc is already drawn on.
export function bez1(a, c, b, t) {
  const u = 1 - t;
  return a * u * u + c * 2 * u * t + b * t * t;
}

// ════════════════════════════════════════════════════════════
// LEVEL 2 SOLVERS — added 2026-07-28
// ════════════════════════════════════════════════════════════
// Same discipline as everything above: camera-relative, written in
// place, zero allocation, no THREE import. A projectile that
// arrives somewhere the guard rose does not live is a projectile
// that lies, and this file exists so none of them can.

// ── THE SKY ── where a comet comes FROM.
// High, upstage and offset toward the half of the frame the answer
// lives on, so a Cinderfall bound for the left of the grid visibly
// starts above the left of the frame. The tell is the sky, and a
// tell you cannot place is not a tell.
export function skyPoint(camera, base, k, out) {
  const d = GUARD.DIRS[k] || GUARD.DIRS.D;
  const m = camera.matrixWorld.elements;
  const side = d.lane * 2.6;
  out.x = base.x + m[0] * side - m[8] * 4.2;
  out.y = (base.y || 0) + 7.4;
  out.z = base.z + m[2] * side - m[10] * 4.2;
  return out;
}

// ── THE RICOCHET ── the Mudlark's coin, and its whole joke.
// caP'n's spec in R-40: "a coin (a spinning disc that RICOCHETS —
// two beziers, one bounce)." So: two beziers, one bounce.
//
// `bouncePoint` is where it meets the boards — short of Perry, on
// the side it was THROWN from, at ground height. The first curve
// runs foe → bounce, the second bounce → guard. The player who
// reads the first half of the flight reads the wrong angle, which
// is exactly the lesson, and it is a lesson you can only teach
// with money because money is the only thing a player will happily
// stare at for four hundred milliseconds.
export function bouncePoint(camera, base, foe, k, out) {
  const m = camera.matrixWorld.elements;
  const dx = (foe?.x ?? base.x) - base.x;
  const dz = (foe?.z ?? base.z) - base.z;
  let lat = m[0] * dx + m[2] * dz;      // which side the throw came from
  if (Math.abs(lat) < 0.001) lat = 1;
  const side = (lat > 0 ? 1 : -1) * 0.95;
  out.x = base.x + m[0] * side - m[8] * 1.15;
  out.y = 0.06;                          // the boards
  out.z = base.z + m[2] * side - m[10] * 1.15;
  return out;
}

// The control point for the SECOND half of a bounce — a low, fast,
// flat-ish rise off the deck into the answer.
export function bounceControl(camera, a, b, out) {
  out.x = (a.x + b.x) * 0.5;
  out.y = Math.max(a.y, b.y) * 0.62 + 0.22;
  out.z = (a.z + b.z) * 0.5;
  return out;
}

// ── THE JET ── a hold has no single arrival point; it has a MOUTH
// and a LINE. This is the mouth: the foe's own centre, chest high,
// which is the one place a stream can plausibly come out of.
export function jetMouth(foe, out, h = 0.86) {
  out.x = foe?.x ?? 0;
  out.y = (foe?.y || 0) + h;
  out.z = foe?.z ?? 0;
  return out;
}
